/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import queryStringEncode from "query-string-encode";
import { stringify as stringifyCsv } from "csv-stringify/browser/esm/sync";
import { last } from "lodash";
import dayjs from "dayjs";
import "dayjs/locale/es";
import "dayjs/locale/fr";
import "dayjs/locale/pt";
import "dayjs/locale/ru";

import SessionStorage from "./services/SessionStorage";
import { Elang } from "./locales";
import { ServerError } from "./index";
import type { DescriptionFields } from "./modules/layers/model";
import { GroupLegendTypes, LayerType } from "./modules/layers/model";
import { TFunction } from "i18next";
import { SxProps, Theme } from "@mui/material/styles";

/**
 * Serialize filters in the format
 *   filter=type==Continent;Jurisdiction,featured==true
 *   note: each group of values is encoded, including valueSeparator
 */
export const serializeFilters = (filters, filterSep = ",", valueSep = ";") => {
  return Object.keys(filters).reduce((acc, key) => {
    const filterGroup = filters[key];
    const value = Array.isArray(filterGroup)
      ? filterGroup.join(valueSep)
      : filterGroup;
    const encodedFilters = [`${key}==${value}`, acc]
      .filter((e) => !!e)
      .join(filterSep);

    return encodedFilters;
  }, "");
};

/**
 * Url encode
 */
export const encodeQueryToURL = (baseUrl, query) =>
  [baseUrl, queryStringEncode(query)].join("?");

export { stringifyCsv };
export const createDataUrlFromBlob = (blob: Blob) => URL.createObjectURL(blob);
export const createDataUrlFromText = (text: string, type?: string) =>
  createDataUrlFromBlob(new Blob([text], type ? { type } : undefined));

/**
 * Download json file
 * @param data
 * @returns {string}
 */
export const downloadJSONFile = (data) =>
  createDataUrlFromText(JSON.stringify(data, null, 2));

/**
 * Download csv file
 * @param data
 */
export const downloadCSVFile = ({ columns = [], rows = [] }) =>
  createDataUrlFromText(
    stringifyCsv(rows, {
      columns,
      header: true,
      cast: { date: getGenericDate },
    }),
    "text/csv"
  );

/**
 * Download tsv file
 * @param data
 */
export const downloadTSVFile = ({ columns = [], rows = [] }) =>
  createDataUrlFromText(
    stringifyCsv(rows, {
      columns,
      header: true,
      delimiter: "\t",
      cast: { date: getGenericDate },
    }),
    "text/tab-separated-values"
  );

const asDayJs = (date: string | Date) =>
  dayjs(date).locale(SessionStorage.get("lang") || Elang.EN);

/**
 * Generic way of rendering a date in our apps YYYY-MM-DD
 */
export const getGenericDate = (date: string | Date): string =>
  asDayJs(date).format("YYYY-MM-DD");

/**
 * Renders dates in a longer form, e.g. "Tuesday, 2 December 2025"
 */
export const getLongFormDate = (date: string | Date): string =>
  asDayJs(date).format("dddd, D MMMM YYYY");

/**
 * Renders dates in a month in short text form, e.g. "2 Dec 2025"
 */
export const getShortMonthDate = (date: string | Date): string =>
  asDayJs(date).format("D MMM YYYY");

/**
 * Renders year and month in a month in short text form, e.g. "Dec 2025"
 */
export const getShortFormMonthYear = (date: string | Date): string =>
  asDayJs(date).format("MMM YYYY");

/**
 * Renders a month and year of a date e.g. "December 2025"
 */
export const getLongFormMonthYear = (date: string | Date): string =>
  asDayJs(date).format("MMMM YYYY");

/**
 * Renders a month only e.g. "December"
 */
export const getLongFormMonth = (date: string | Date): string =>
  asDayJs(date).format("MMMM");

/**
 * Renders a month only e.g. "December"
 */
export const getYear = (date: string | Date): string =>
  asDayJs(date).format("YYYY");

/**
 * Get initials from user name
 */
export const getInitials = (name: string): string => {
  const emptySpace = " ";
  const [firstName = emptySpace, ...rest] = name.split(emptySpace);
  const lastName = last(rest) || emptySpace;

  return `${firstName[0]}${lastName[0]}`.toUpperCase();
};

/**
 * Boilerplate citation for the public UNBL workspace, asking users to cite both
 * the original data providers and the UNBL platform itself. Only the
 * introductory sentence is translatable; the formal citation (publisher, URL,
 * DOI) is kept in its canonical form. The year and access date reflect the
 * current date.
 */
export const getUnblPlatformCitationText = (
  t: (key: string) => string
): string => {
  const now = new Date();
  const year = now.getFullYear();
  const accessedDate = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(now);
  const intro = t(
    "If you use any maps, charts or statistics generated by the UN Biodiversity Lab platform, please cite both the original data providers and UN Biodiversity Lab:"
  );
  const citation =
    `UN Biodiversity Lab (${year}). ` +
    `<a href="https://unbiodiversitylab.org">https://unbiodiversitylab.org</a>. ` +
    `Accessed ${accessedDate}. ` +
    `<a href="https://doi.org/10.34892/95Q9-MP91">https://doi.org/10.34892/95Q9-MP91</a>`;
  return `<em>${intro}</em><p>${citation}</p>`;
};

/**
 * Appends the UNBL platform citation boilerplate to a layer's citation when the
 * layer belongs to the public UNBL workspace. Returns the citation unchanged
 * otherwise.
 */
export const withUnblPlatformCitation = (
  citation: string | undefined,
  isPublicOrgLayer: boolean,
  t: (key: string) => string
): string | undefined => {
  if (!isPublicOrgLayer) {
    return citation;
  }
  const platformCitation = getUnblPlatformCitationText(t);
  return citation
    ? `${citation}<p>${platformCitation}</p>`
    : `<p>${platformCitation}</p>`;
};

/**
 * Add target="_blank" to all links in a HTML string
 */
export const addBlankToLinks = (html: string) => {
  if (!html) {
    return html;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const links = doc.querySelectorAll("a");

  links.forEach((link) => {
    link.setAttribute("target", "_blank");
  });

  return doc.body.innerHTML;
};

/**
 * Returns `true` if we are rendering client-side, `false` when we are rendering
 * on the server.
 */
export const isBrowser = () => typeof window !== "undefined";

const PRELOAD_RELOAD_KEY = "preload-error-reload";

/**
 * A deploy deletes the hashed chunks an open tab still references, so reload
 * when one fails to load. At most once a minute: a chunk that is really broken
 * then surfaces its error instead of reloading in a loop.
 */
export const reloadOnPreloadError = () => {
  window.addEventListener("vite:preloadError", () => {
    try {
      const lastReload = Number(sessionStorage.getItem(PRELOAD_RELOAD_KEY));

      if (Date.now() - lastReload > 60 * 1000) {
        sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(Date.now()));
        window.location.reload();
      }
    } catch {
      // storage blocked: without the rate limit a reload could loop, so skip it
    }
  });
};

/**
 * This helper wrapper will call `event.stopPropagation();` and then defer to
 * the original handler. This is useful e.g. when an element with a click
 * handler is nested inside an event without
 *
 * ```jsx
 * <OuterControl
 *   onClick={onOuterClick}
 * >
 *   <Button
 *     onClick={eventStopPropagation(onInnerClick)}
 *   />
 * </OuterControl>
 * ```
 */
export const eventStopPropagation =
  (handler = null) =>
  (e, ...args) => {
    e.stopPropagation();

    return handler?.(e, ...args);
  };

/**
 * This helper wrapper will call `event.preventDefault();` and then defer to
 * the original handler. This is useful for stopping default actions like
 * form submissions being triggered.
 *
 * ```jsx
 * <Button
 *   onClick={eventPreventDefault(onButtonPress)}
 * >
 * ```
 */
export const eventPreventDefault =
  (handler) =>
  (e, ...args) => {
    e.preventDefault();

    return handler(e, ...args);
  };

/**
 * Helper for avoiding race conditions where multiple asynchronous operations
 * result in out-of-sequence state mutations.
 *
 * ```js
 * useEffect(
 *   withEffectLock(
 *     (getHasEffectLock) => {
 *       return doSomethingSlowly().then(
 *         (result) => {
 *           if (getHasEffectLock) {
 *             setData(result)
 *           }
 *           // otherwise the effect has already been cleaned up, e.g. due to
 *           // the component being unmounted or effect dependencies changing
 *           // again.
 *         }
 *       )
 *     }
 *   )
 * ),
 * ```
 */
export const withEffectLock =
  (effectFunction: (getHasLock: () => boolean) => void | (() => undefined)) =>
  () => {
    const hasLockWrapper = [true];
    const getHasLock = () => hasLockWrapper[0];

    const cleanup = effectFunction(getHasLock);

    return () => {
      hasLockWrapper[0] = false;

      return cleanup && cleanup();
    };
  };

export const extractServerErrors = (
  error: any,
  t: TFunction
): ServerError[] => {
  // This represents error messages which are (not) passed by the backend. E.g. Network errors
  const fallbackError: ServerError[] = [
    {
      detail: t("Something went wrong"),
    },
  ];

  return error?.data?.errors && Array.isArray(error.data.errors)
    ? (error.data.errors as ServerError[])
    : fallbackError;
};

const wordBreakValues = {
  wordBreak: "break-word",
  overflowWrap: "break-word",
  whiteSpace: "normal",
} as const;

export const wordBreakSx: SxProps<Theme> = wordBreakValues;
export const wordBreakStyle: React.CSSProperties = wordBreakValues;

const bandToHex = (c) => {
  const hex = c.toString(16);
  return hex.length === 1 ? `0${hex}` : hex;
};

/**
 * Convert [r,g,b] array to hex color value
 * @param value
 */
export const rgbToHex = ([r = 255, g = 255, b = 255, a = 1]) => {
  const alphaValue = Math.round(255 * a);

  return `#${bandToHex(r)}${bandToHex(g)}${bandToHex(b)}${bandToHex(
    alphaValue
  )}`;
};

// Unset also covers objects with no set entries: the admin saves empty
// partial dates as {year: null, month: null, day: null}, which must not block
// inheriting an ancestor's real date.
export const hasMetadataValue = (value: any): boolean => {
  if (value === null || value === undefined || value === "") {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === "object") {
    return Object.values(value).some(
      (entry) => entry !== null && entry !== undefined
    );
  }
  return true;
};

export interface IInheritedLayerMetadata extends DescriptionFields {
  description: string;
  translationReviewed?: boolean;
}

/**
 * Combines translation-review flags for text assembled from several layers:
 * unreviewed if any contributing flag is false, undefined when none is set
 * (English, or no translation entries).
 */
export const combineTranslationReviewed = (
  ...flags: any[]
): boolean | undefined => {
  const set = flags.filter((flag) => typeof flag === "boolean");
  return set.length ? set.every(Boolean) : undefined;
};

/**
 * Ancestor-group metadata for a grouped layer (walking the `parent` chain,
 * nearest ancestor first), shared by the map and the admin previews so both
 * resolve inheritance identically: `description` is every ancestor's
 * concatenated top-down (ready to prepend to the layer's own); every other
 * field is the nearest ancestor's set value, to be used when the layer's own
 * is unset. `fieldOf` customizes how a field is read from an ancestor (the
 * admin prefers stored translations). `translationReviewed` is the ancestors'
 * flags combined (see combineTranslationReviewed).
 */
export const getInheritedLayerMetadata = (
  parent: any,
  fieldOf: (node: any, field: string) => any = (node, field) => node?.[field]
): IInheritedLayerMetadata => {
  const ancestors: any[] = [];
  for (let node = parent; node; node = node.parent) {
    ancestors.push(node);
  }
  const inherit = (field: string) =>
    ancestors.map((node) => fieldOf(node, field)).find(hasMetadataValue);
  const inheritExtra = (field: string) =>
    ancestors
      .map((node) => node?.extraMetadata?.[field])
      .find(hasMetadataValue);

  return {
    description: [...ancestors]
      .reverse()
      .map((node) => fieldOf(node, "description"))
      .filter(hasMetadataValue)
      .join(""),
    sources: inherit("sources"),
    citation: inherit("citation"),
    license: inherit("license"),
    disclaimer: inherit("disclaimer"),
    readMoreLinks: inherit("readMoreLinks"),
    downloadLinks: inherit("downloadLinks"),
    resolutionMeters: inheritExtra("resolutionMeters"),
    dataVersion: inheritExtra("dataVersion"),
    startDate: inheritExtra("startDate"),
    endDate: inheritExtra("endDate"),
    publicationDate: inheritExtra("publicationDate"),
    translationReviewed: combineTranslationReviewed(
      ...ancestors.map((node) => node?.translationReviewed)
    ),
  };
};

/**
 * True for a group layer whose child selector is the timeline. As in admin, a
 * `source.type` of group means radio buttons whatever legendType says. Reads
 * the map's flattened layers and admin's `config` alike. Only works on a raw
 * layer: in the map legend `layers[0].legendConfig` is the child's.
 */
export const isGroupTimeline = (layer: any): boolean => {
  const legendConfig = layer?.legendConfig ?? layer?.config?.legendConfig;
  const source = layer?.source ?? layer?.config?.source;
  return (
    layer?.type === LayerType.group &&
    legendConfig?.legendType === GroupLegendTypes.TIMELINE &&
    source?.type !== "group"
  );
};

// Only groups of groups expand in the map's dataset list and are therefore
// not directly activatable via ?layers= links. A timeline group is a leaf to
// its parent, whose selector picks it like any other child. References beyond
// the API's populate depth are bare id strings and don't count.
export const isExpandableGroup = (layer: any): boolean =>
  layer?.type === "group" &&
  (layer.references ?? []).some(
    (ref: any) =>
      ref &&
      typeof ref === "object" &&
      ref.type === "group" &&
      !isGroupTimeline(ref)
  );

/**
 * Sort-of the opposite of lodash.toPath
 *
 * concatFormAccessor(['items', 0, 'name']) => 'items[0].name'
 * concatFormAccessor(['name'], 'items[0]') => 'items[0].name'
 */
export const concatFormAccessor = (
  steps: Array<string | number>,
  prefix: string = ""
) =>
  prefix.concat(
    steps
      .filter((step) => step || step === 0)
      .map((step, i) => {
        const dotBefore = prefix || i ? "." : "";

        if (Number.isSafeInteger(step)) {
          return `${dotBefore}[${step}]`;
        }

        if (/[\W]/.test(step as string)) {
          return `${dotBefore}["${String(step).replace(/(\W)/g, "\\$1")}"]`;
        }

        return `${dotBefore}[${step}]`;
      })
      .join("")
  );
